package com.xtream.composeapp.presentation.ui.tvchannels

import androidx.compose.foundation.focusGroup
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.focusRestorer
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.xtream.composeapp.viewmodel.livetv.StreamControlViewModel
import com.xtream.data_kmp.AllConst
import com.xtream.data_kmp.dto.LiveCategory
import com.xtream.sharedResources.resources.Res
import com.xtream.sharedResources.resources.categories
import com.xtream.ui.ui.widget.FocusPositioner
import com.xtream.ui.ui.widget.NavigationButton
import com.xtream.ui.ui.widget.TvButton
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource


@Composable
fun TvCategoriesTvList(
    modifier: Modifier = Modifier,
    title: String= stringResource(Res.string.categories),
    onSameItemSelected: (() -> Unit)?=null,
    onClick: ((LiveCategory) -> Unit)?=null,
    streamControlViewModel: StreamControlViewModel,
    categoriesUiState: List<LiveCategory>,
    playingCategory: LiveCategory?,
    includeDefaultCategories: Boolean=true,
    isFullScreen: Boolean = false,
     onNavigationIconClick: (() -> Unit)?=null,

) {
    val scope = rememberCoroutineScope()
    var focusJob by remember { mutableStateOf<Job?>(null) }
    val selectedCategory by streamControlViewModel.selectedTvGroup.collectAsStateWithLifecycle()
    val density = LocalDensity.current

    // ── Stable flat list ──────────────────────────────────────────────────────
    val allItems = remember(categoriesUiState) {
        if (includeDefaultCategories)
        AllConst.defaultCategories + categoriesUiState
        else
            categoriesUiState

    }

    // ── One FocusRequester per item, keyed by stable ID ───────────────────────
    val focusRequesters = remember(allItems) {
        allItems.associate { item ->
            (item.categoryId ?: item.categoryName ?: "") to FocusRequester()
        }
    }

    // ── Restore target: only follow selectedCategory, never playingCategory ───
    // playingCategory is only used for the `selected` highlight (visual only).
    // Using it here is what caused the focus steal when switching channels.
    val restoreTargetKey by remember(selectedCategory, allItems) {
        derivedStateOf {
            val targetId = selectedCategory?.categoryId
            allItems.firstOrNull { it.categoryId == targetId }
                ?.let { it.categoryId ?: it.categoryName ?: "" }
        }
    }

    val selectedIndex = remember(allItems, selectedCategory) {
        allItems.indexOfFirst { it.categoryId == selectedCategory?.categoryId }
            .coerceAtLeast(0)
    }

    val listState = rememberLazyListState(selectedIndex, 0)

    // ── Scroll to center + request focus ONCE on initial load ─────────────────
    // Guard flag prevents re-stealing focus on every allItems change
    // (e.g. when playingCategory/selectedCategory updates after channel switch).
    var hasInitialFocus by remember { mutableStateOf(false) }

    LaunchedEffect(allItems) {
        val layoutInfo = listState.layoutInfo
        val viewportHeight =
            layoutInfo.viewportEndOffset + layoutInfo.viewportStartOffset
        val itemHeightPx = with(density) { 55.dp.toPx() }
        val centerOffset = ((viewportHeight - itemHeightPx) / 2).toInt()
        listState.scrollToItem(selectedIndex, -centerOffset)

        if (!hasInitialFocus) {
            hasInitialFocus = true
            restoreTargetKey
                .let { focusRequesters[it] }
                ?.requestFocus()
        }
    }

    Scaffold(
        modifier = modifier,
        topBar = {
            @OptIn(ExperimentalMaterial3Api::class)
            TopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.background
                ),
                title = {
                    Text(
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = TextAlign.Center,
                        text = title,
                        style = MaterialTheme.typography.titleLarge
                    )
                },
                navigationIcon = {
                    onNavigationIconClick.takeIf { it!=null }?.let {
                        NavigationButton(
                            onClick = it,
                            colors =  IconButtonDefaults.iconButtonColors(
                                contentColor = MaterialTheme.colorScheme.onBackground
                            )

                        )
                    }

                }
            )
        },

    ) { innerPadding ->

        val containerFocusRequester = remember { FocusRequester() }

        FocusPositioner(
            parentFraction = 0.5f,
            childFraction = 0.5f,
        ) {
            val density = LocalDensity.current
            val screenHeightDp = with(density) {
                LocalWindowInfo.current.containerSize.height.toDp()
            }
            LazyColumn(
                state = listState,
                modifier = Modifier
                    .focusRequester(containerFocusRequester)
                    .focusGroup()
                    // Lambda form: evaluated fresh on each restore trigger,
                    // so it always points at the correct requester even after
                    // selectedCategory changes.
                    .focusRestorer(
                        focusRequesters[restoreTargetKey]
                            ?: FocusRequester.Default
                    )
                    // IMPORTANT_UNCOMMENT_IN_NEXT_UPDATES
                    // .focusProperties {
                    //     onEnter = { if (isFullScreen) cancelFocusChange() }
                    // }
                    .fillMaxSize(),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                contentPadding = PaddingValues(
                    top = innerPadding.calculateTopPadding() + 8.dp,
                    bottom = screenHeightDp/2,
                    start = 4.dp, end = 4.dp
                )
            ) {
                itemsIndexed(
                    allItems,
                    key = { index, item ->
                        "cat_${item.categoryId ?: item.categoryName ?: index}"
                    }
                ) { _, item ->
                    val itemKey = item.categoryId ?: item.categoryName ?: ""
                    // Safe fallback: should never be needed since map is built
                    // from allItems, but guards against race during recomposition.
                    val itemFocusRequester = focusRequesters[itemKey]
                        ?: remember { FocusRequester() }

                    TvButton(
                        selected = playingCategory?.categoryId == item.categoryId,
                        modifier = Modifier
                            .focusRequester(itemFocusRequester)
                            .onFocusChanged { focusState ->
                                if (focusState.isFocused) {
                                    focusJob?.cancel()
                                    focusJob = scope.launch {
                                        delay(400)
                                        streamControlViewModel.emitGroup(item)
                                    }
                                }
                            },
                        fillParent = true,
                        title = item.categoryName ?: "",
                        onClick = {
                            if (onSameItemSelected!=null && item.categoryId == streamControlViewModel.selectedTvGroup.value?.categoryId)
                                onSameItemSelected()
                            else
                                onClick?.invoke(item)
                        }
                    )
                }
            }
        }
    }
}